import {
  Box,
  Card,
  Column,
  Heading,
  Icon,
  Row,
  Stack,
  TextLinkButton
} from '@te-whatu-ora/anatomic'
import { Show } from '@/components/show'
import type { PageLayoutProps } from '@healthnz-prm/fhir-engine-renderer/types'
import { Children } from 'react'
import { ModalManager } from '@/components/modalManager/ModalManager'
import { ContentLoader } from '@/components/shared'
import { NextSubmitButtons } from '@/components/fhirEngine/nextSubmitButtons'
import { useSubmitState } from '@/contexts/submitState/submitState'
import { useEmbed } from '@/contexts/embed/embed'

export default function PageLayout({
  children,
  isPartialSaveLoading,
  questionnaireTitle,
  questionnaireTitleComponent,
  onPrev,
  onNext,
  onSubmit,
  submitButtonLabel,
  title,
  validate
}: PageLayoutProps) {
  const { submitState } = useSubmitState()
  const embed = useEmbed()

  const handleNext =
    onNext &&
    (() => {
      onNext()
      embed.onPageChange()
    })

  const handlePrev =
    onPrev &&
    (() => {
      onPrev()
      embed.onPageChange()
    })

  return (
    <main>
      <Show
        when={
          !embed.isEmbedded &&
          (questionnaireTitleComponent || questionnaireTitle)
        }
      >
        <Box paddingBottom='small'>
          <Heading level='2' color='primary100' as='h1'>
            {questionnaireTitleComponent ?? questionnaireTitle}
          </Heading>
        </Box>
      </Show>
      <ModalManager />
      <Row>
        <Column columns={12}>
          <Show when={['ready', 'invalid'].includes(submitState)}>
            <Show
              when={onPrev}
              fallback={
                <Show when={embed.showQuitButton}>
                  <Box marginBottom='xsmall'>
                    <TextLinkButton onPress={() => embed.onExit('back')}>
                      <Box display='flex' alignItems='center'>
                        <Icon
                          icon='arrow_left'
                          marginRight='xsmall'
                          variant='functionalIcons'
                        />
                        Back
                      </Box>
                    </TextLinkButton>
                  </Box>
                </Show>
              }
            >
              <Box marginBottom='xsmall'>
                <TextLinkButton onPress={handlePrev}>
                  <Box display='flex' alignItems='center'>
                    <Icon
                      icon='arrow_left'
                      marginRight='xsmall'
                      variant='functionalIcons'
                    />
                    Previous step
                  </Box>
                </TextLinkButton>
              </Box>
            </Show>
          </Show>
          <Show when={title}>
            <Box marginBottom='xsmall'>
              <Heading level='3' color='primary100' as='h1'>
                {title}
              </Heading>
            </Box>
          </Show>
          <ContentLoader state={submitState} loadingMessage='Submitting'>
            <Stack space='medium'>
              {Children.map(children, (item, itemIndex) => (
                <Card key={itemIndex}>
                  {item}
                  <Show when={itemIndex === Children.count(children) - 1}>
                    <NextSubmitButtons
                      hasSubmit={!!onSubmit}
                      isPartialSaveLoading={isPartialSaveLoading}
                      onNext={handleNext}
                      submitButtonLabel={submitButtonLabel}
                      submitState={submitState}
                      validate={validate}
                    />
                    <Show
                      when={embed.showQuitButton && submitState !== 'success'}
                    >
                      <Box
                        style={{ display: 'flex', justifyContent: 'center' }}
                        paddingTop='small'
                      >
                        <TextLinkButton onPress={() => embed.onExit('quit')}>
                          Quit
                        </TextLinkButton>
                      </Box>
                    </Show>
                  </Show>
                </Card>
              ))}
            </Stack>
          </ContentLoader>
        </Column>
      </Row>
    </main>
  )
}
